import { useEffect, useState } from 'react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { formatDate } from '@/utils/formatters';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CreditCard, Plus } from 'lucide-react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { PaymentInstrument } from './types';

interface CostCenter {
    id: string;
    name: string;
}

interface CompanyOption {
    id: string;
    name: string;
}

interface PaymentInstrumentFormData {
    name: string;
    instrument_type: string;
    account_number: string;
    iban: string;
    bank_name: string;
    cost_center_id: string;
    company_id: string;
    is_active: boolean;
}

interface PaymentInstrumentsTabProps {
    instruments: PaymentInstrument[];
    loading: boolean;
    error: string | null;
    onDelete: (id: string) => void;
    onCreated: () => void;
}

const getInstrumentTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
        case 'bank_account':
        case 'credit_card':
        case 'cash':
        default:
            return <CreditCard className="h-4 w-4" />;
    }
};

const emptyFormData: PaymentInstrumentFormData = {
    name: '',
    instrument_type: '',
    account_number: '',
    iban: '',
    bank_name: '',
    cost_center_id: '',
    company_id: '',
    is_active: true,
};

const PaymentInstrumentsTab = ({ instruments, loading, error, onDelete, onCreated }: PaymentInstrumentsTabProps) => {
    const { t } = useI18n();
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState<PaymentInstrumentFormData>(emptyFormData);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editFormData, setEditFormData] = useState<PaymentInstrumentFormData>(emptyFormData);
    const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});
    const [editSubmitError, setEditSubmitError] = useState<string | null>(null);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [refsLoading, setRefsLoading] = useState(false);
    const [refsLoaded, setRefsLoaded] = useState(false);

    useEffect(() => {
        if ((!showForm && !showEditForm) || refsLoaded) return;
        setRefsLoading(true);
        Promise.all([
            apiFetch('/api/cost-centers/')
                .then((resp) => resp.ok ? resp.json() : [])
                .then((data) => setCostCenters(Array.isArray(data) ? data : []))
                .catch(() => setCostCenters([])),
            apiFetch('/api/companies/')
                .then((resp) => resp.ok ? resp.json() : [])
                .then((data) => setCompanies(Array.isArray(data) ? data : []))
                .catch(() => setCompanies([])),
        ])
            .finally(() => {
                setRefsLoading(false);
                setRefsLoaded(true);
            });
    }, [showForm, showEditForm, refsLoaded]);

    const getInstrumentTypeName = (type: string) => {
        switch (type.toLowerCase()) {
            case 'bank_account':
                return t('finance.paymentInstruments.types.bank_account', 'admin');
            case 'credit_card':
                return t('finance.paymentInstruments.types.credit_card', 'admin');
            case 'cash':
                return t('finance.paymentInstruments.types.cash', 'admin');
            default:
                return type;
        }
    };

    const openForm = () => {
        setShowForm(true);
        setFormData(emptyFormData);
        setFormErrors({});
        setSubmitError(null);
    };

    const closeForm = () => {
        setShowForm(false);
        setFormData(emptyFormData);
        setFormErrors({});
        setSubmitError(null);
    };

    const openEditForm = async (id: string) => {
        setShowEditForm(true);
        setEditSubmitError(null);
        setEditFormErrors({});
        setEditFormData(emptyFormData);
        setEditingId(id);
        setEditLoading(true);
        try {
            const response = await apiFetch(`/api/payment-instruments/${id}`);
            if (!response.ok) {
                setEditSubmitError(t('finance.paymentInstruments.loadFailed', 'admin'));
                return;
            }
            const data = await response.json();
            setEditFormData({
                name: data.name || '',
                instrument_type: data.instrument_type || '',
                account_number: data.account_number || '',
                iban: data.iban || '',
                bank_name: data.bank_name || '',
                cost_center_id: data.cost_center_id || '',
                company_id: data.company_id || data.company?.id || '',
                is_active: data.is_active ?? true,
            });
        } catch (err) {
            console.error('Error fetching payment instrument:', err);
            setEditSubmitError(t('finance.paymentInstruments.loadFailed', 'admin'));
        } finally {
            setEditLoading(false);
        }
    };

    const closeEditForm = () => {
        setShowEditForm(false);
        setEditFormData(emptyFormData);
        setEditFormErrors({});
        setEditSubmitError(null);
        setEditingId(null);
    };

    const handleInputChange = (field: keyof PaymentInstrumentFormData, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handleEditInputChange = (field: keyof PaymentInstrumentFormData, value: string | boolean) => {
        setEditFormData((prev) => ({ ...prev, [field]: value }));
        if (editFormErrors[field]) {
            setEditFormErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};
        if (!formData.name.trim()) errors.name = t('forms.required', 'common');
        if (!formData.instrument_type) errors.instrument_type = t('forms.required', 'common');
        if (!formData.company_id) errors.company_id = t('forms.required', 'common');
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateEditForm = () => {
        const errors: Record<string, string> = {};
        if (!editFormData.name.trim()) errors.name = t('forms.required', 'common');
        if (!editFormData.instrument_type) errors.instrument_type = t('forms.required', 'common');
        if (!editFormData.company_id) errors.company_id = t('forms.required', 'common');
        setEditFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            const submitData = {
                name: formData.name.trim(),
                instrument_type: formData.instrument_type,
                account_number: formData.account_number.trim() || undefined,
                iban: formData.iban.trim() || undefined,
                bank_name: formData.bank_name.trim() || undefined,
                cost_center_id: formData.cost_center_id || undefined,
                company_id: formData.company_id || undefined,
                is_active: formData.is_active,
            };

            const response = await apiFetch('/api/payment-instruments/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                setSubmitError(t('finance.paymentInstruments.createFailed', 'admin'));
                return;
            }

            closeForm();
            onCreated();
        } catch (err) {
            console.error('Error creating payment instrument:', err);
            setSubmitError(t('finance.paymentInstruments.createFailed', 'admin'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEditSubmitError(null);
        if (!editingId || !validateEditForm()) return;
        setEditSubmitting(true);
        try {
            const submitData = {
                name: editFormData.name.trim(),
                instrument_type: editFormData.instrument_type,
                account_number: editFormData.account_number.trim() || undefined,
                iban: editFormData.iban.trim() || undefined,
                bank_name: editFormData.bank_name.trim() || undefined,
                cost_center_id: editFormData.cost_center_id || undefined,
                company_id: editFormData.company_id || undefined,
                is_active: editFormData.is_active,
            };

            const response = await apiFetch(`/api/payment-instruments/${editingId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                setEditSubmitError(t('finance.paymentInstruments.updateFailed', 'admin'));
                return;
            }

            closeEditForm();
            onCreated();
        } catch (err) {
            console.error('Error updating payment instrument:', err);
            setEditSubmitError(t('finance.paymentInstruments.updateFailed', 'admin'));
        } finally {
            setEditSubmitting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">{t('finance.paymentInstruments.title', 'admin')}</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('finance.paymentInstruments.description', 'admin')}
                    </p>
                </div>
                <Button onClick={openForm} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    {t('finance.paymentInstruments.add', 'admin')}
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {loading ? (
                <div>{t('actions.loading', 'common')}</div>
            ) : (
                <div className="border rounded overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('finance.paymentInstruments.name', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.type', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.accountNumber', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.bankName', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.company', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.costCenter', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.status', 'admin')}</TableHead>
                                <TableHead>{t('finance.paymentInstruments.created', 'admin')}</TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {instruments.map((instrument) => (
                                <TableRow key={instrument.id}>
                                    <TableCell>
                                        <div className="flex items-center">
                                            {getInstrumentTypeIcon(instrument.instrument_type)}
                                            <div className="ml-3">{instrument.name}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell>{getInstrumentTypeName(instrument.instrument_type)}</TableCell>
                                    <TableCell>{instrument.account_number || '-'}</TableCell>
                                    <TableCell>{instrument.bank_name || '-'}</TableCell>
                                    <TableCell>{instrument.company?.name || '-'}</TableCell>
                                    <TableCell>{instrument.cost_center?.name || t('finance.paymentInstruments.allCenters', 'admin')}</TableCell>
                                    <TableCell>
                                         <Badge variant={instrument.is_active ? 'default' : 'secondary'}>
                                                {instrument.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                            </Badge>
                                    </TableCell>
                                    <TableCell>{formatDate(instrument.created_at)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" onClick={() => openEditForm(instrument.id)}>
                                                <PencilIcon className="h-4 w-4" />
                                            </Button>
                                            <Button variant="outline" onClick={()=>onDelete(instrument.id)}>
                                                <TrashIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {instruments.length === 0 && (
                                <TableRow>
                                    <TableCell className="text-gray-500 py-4" colSpan={9}>
                                        {t('finance.paymentInstruments.noData', 'admin')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white w-full max-w-4xl rounded shadow-lg p-6 space-y-4 my-8">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {t('finance.paymentInstruments.newTitle', 'admin')}
                            </h2>
                            <button onClick={closeForm} className="text-gray-600 hover:text-black">✕</button>
                        </div>

                        {submitError && <div className="text-red-600 text-sm">{submitError}</div>}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="name">{t('finance.paymentInstruments.fields.name', 'admin')}</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        placeholder={t('finance.paymentInstruments.placeholders.name', 'admin')}
                                        className={formErrors.name ? 'border-red-500' : ''}
                                    />
                                    {formErrors.name && <p className="text-sm text-red-600">{formErrors.name}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="instrument_type">{t('finance.paymentInstruments.fields.type', 'admin')}</Label>
                                    <Select
                                        value={formData.instrument_type}
                                        onValueChange={(value) => handleInputChange('instrument_type', value)}
                                    >
                                        <SelectTrigger className={formErrors.instrument_type ? 'border-red-500' : ''}>
                                            <SelectValue placeholder={t('finance.paymentInstruments.placeholders.type', 'admin')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="bank_account">{t('finance.paymentInstruments.types.bank_account', 'admin')}</SelectItem>
                                            <SelectItem value="credit_card">{t('finance.paymentInstruments.types.credit_card', 'admin')}</SelectItem>
                                            <SelectItem value="cash">{t('finance.paymentInstruments.types.cash', 'admin')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {formErrors.instrument_type && (
                                        <p className="text-sm text-red-600">{formErrors.instrument_type}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="account_number">{t('finance.paymentInstruments.fields.accountNumber', 'admin')}</Label>
                                    <Input
                                        id="account_number"
                                        value={formData.account_number}
                                        onChange={(e) => handleInputChange('account_number', e.target.value)}
                                        placeholder={t('finance.paymentInstruments.placeholders.accountNumber', 'admin')}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="iban">{t('finance.paymentInstruments.fields.iban', 'admin')}</Label>
                                    <Input
                                        id="iban"
                                        value={formData.iban}
                                        onChange={(e) => handleInputChange('iban', e.target.value)}
                                        placeholder={t('finance.paymentInstruments.placeholders.iban', 'admin')}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="company_id">{t('finance.paymentInstruments.fields.company', 'admin')}</Label>
                                    <Select
                                        value={formData.company_id}
                                        onValueChange={(value) => handleInputChange('company_id', value)}
                                    >
                                        <SelectTrigger className={formErrors.company_id ? 'border-red-500' : ''}>
                                            <SelectValue placeholder={t('finance.paymentInstruments.placeholders.company', 'admin')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {refsLoading && companies.length === 0 ? (
                                                <SelectItem value="loading" disabled>
                                                    {t('finance.paymentInstruments.loadingCompanies', 'admin')}
                                                </SelectItem>
                                            ) : (
                                                companies.map((c) => (
                                                    <SelectItem key={c.id} value={c.id}>
                                                        {c.name}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                    {formErrors.company_id && (
                                        <p className="text-sm text-red-600">{formErrors.company_id}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="bank_name">{t('finance.paymentInstruments.fields.bankName', 'admin')}</Label>
                                    <Input
                                        id="bank_name"
                                        value={formData.bank_name}
                                        onChange={(e) => handleInputChange('bank_name', e.target.value)}
                                        placeholder={t('finance.paymentInstruments.placeholders.bankName', 'admin')}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="cost_center_id">{t('finance.paymentInstruments.fields.costCenterOptional', 'admin')}</Label>
                                    <Select
                                        value={formData.cost_center_id || 'all'}
                                        onValueChange={(value) => handleInputChange('cost_center_id', value === 'all' ? '' : value)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('finance.paymentInstruments.allCenters', 'admin')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('finance.paymentInstruments.allCenters', 'admin')}</SelectItem>
                                            {refsLoading && costCenters.length === 0 ? (
                                                <SelectItem value="loading" disabled>
                                                    {t('finance.paymentInstruments.loadingCostCenters', 'admin')}
                                                </SelectItem>
                                            ) : (
                                                costCenters.map((costCenter) => (
                                                    <SelectItem key={costCenter.id} value={costCenter.id}>
                                                        {costCenter.name}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="is_active"
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => handleInputChange('is_active', checked as boolean)}
                                    />
                                    <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4">
                                <Button type="button" variant="outline" onClick={closeForm}>
                                    {t('actions.cancel', 'common')}
                                </Button>
                                <Button type="submit" disabled={submitting}>
                                    {submitting
                                        ? t('finance.paymentInstruments.creating', 'admin')
                                        : t('finance.paymentInstruments.createAction', 'admin')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showEditForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white w-full max-w-4xl rounded shadow-lg p-6 space-y-4 my-8">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {t('finance.paymentInstruments.editTitle', 'admin')}
                            </h2>
                            <button onClick={closeEditForm} className="text-gray-600 hover:text-black">✕</button>
                        </div>

                        {editSubmitError && <div className="text-red-600 text-sm">{editSubmitError}</div>}

                        {editLoading ? (
                            <div>{t('actions.loading', 'common')}</div>
                        ) : (
                            <form onSubmit={handleEditSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="edit_name">{t('finance.paymentInstruments.fields.name', 'admin')}</Label>
                                        <Input
                                            id="edit_name"
                                            value={editFormData.name}
                                            onChange={(e) => handleEditInputChange('name', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.name', 'admin')}
                                            className={editFormErrors.name ? 'border-red-500' : ''}
                                        />
                                        {editFormErrors.name && <p className="text-sm text-red-600">{editFormErrors.name}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="edit_instrument_type">{t('finance.paymentInstruments.fields.type', 'admin')}</Label>
                                        <Select
                                            value={editFormData.instrument_type}
                                            onValueChange={(value) => handleEditInputChange('instrument_type', value)}
                                        >
                                            <SelectTrigger className={editFormErrors.instrument_type ? 'border-red-500' : ''}>
                                                <SelectValue placeholder={t('finance.paymentInstruments.placeholders.type', 'admin')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="bank_account">{t('finance.paymentInstruments.types.bank_account', 'admin')}</SelectItem>
                                                <SelectItem value="credit_card">{t('finance.paymentInstruments.types.credit_card', 'admin')}</SelectItem>
                                                <SelectItem value="cash">{t('finance.paymentInstruments.types.cash', 'admin')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {editFormErrors.instrument_type && (
                                            <p className="text-sm text-red-600">{editFormErrors.instrument_type}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="edit_account_number">{t('finance.paymentInstruments.fields.accountNumber', 'admin')}</Label>
                                        <Input
                                            id="edit_account_number"
                                            value={editFormData.account_number}
                                            onChange={(e) => handleEditInputChange('account_number', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.accountNumber', 'admin')}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="edit_iban">{t('finance.paymentInstruments.fields.iban', 'admin')}</Label>
                                        <Input
                                            id="edit_iban"
                                            value={editFormData.iban}
                                            onChange={(e) => handleEditInputChange('iban', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.iban', 'admin')}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="edit_company_id">{t('finance.paymentInstruments.fields.company', 'admin')}</Label>
                                        <Select
                                            value={editFormData.company_id}
                                            onValueChange={(value) => handleEditInputChange('company_id', value)}
                                        >
                                            <SelectTrigger className={editFormErrors.company_id ? 'border-red-500' : ''}>
                                                <SelectValue placeholder={t('finance.paymentInstruments.placeholders.company', 'admin')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {refsLoading && companies.length === 0 ? (
                                                    <SelectItem value="loading" disabled>
                                                        {t('finance.paymentInstruments.loadingCompanies', 'admin')}
                                                    </SelectItem>
                                                ) : (
                                                    companies.map((c) => (
                                                        <SelectItem key={c.id} value={c.id}>
                                                            {c.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                        {editFormErrors.company_id && (
                                            <p className="text-sm text-red-600">{editFormErrors.company_id}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="edit_bank_name">{t('finance.paymentInstruments.fields.bankName', 'admin')}</Label>
                                        <Input
                                            id="edit_bank_name"
                                            value={editFormData.bank_name}
                                            onChange={(e) => handleEditInputChange('bank_name', e.target.value)}
                                            placeholder={t('finance.paymentInstruments.placeholders.bankName', 'admin')}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="edit_cost_center_id">{t('finance.paymentInstruments.fields.costCenterOptional', 'admin')}</Label>
                                        <Select
                                            value={editFormData.cost_center_id || 'all'}
                                            onValueChange={(value) => handleEditInputChange('cost_center_id', value === 'all' ? '' : value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t('finance.paymentInstruments.allCenters', 'admin')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('finance.paymentInstruments.allCenters', 'admin')}</SelectItem>
                                                {refsLoading && costCenters.length === 0 ? (
                                                    <SelectItem value="loading" disabled>
                                                        {t('finance.paymentInstruments.loadingCostCenters', 'admin')}
                                                    </SelectItem>
                                                ) : (
                                                    costCenters.map((costCenter) => (
                                                        <SelectItem key={costCenter.id} value={costCenter.id}>
                                                            {costCenter.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="edit_is_active"
                                            checked={editFormData.is_active}
                                            onCheckedChange={(checked) => handleEditInputChange('is_active', checked as boolean)}
                                        />
                                        <Label htmlFor="edit_is_active">{t('status.active', 'common')}</Label>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-4">
                                    <Button type="button" variant="outline" onClick={closeEditForm}>
                                        {t('actions.cancel', 'common')}
                                    </Button>
                                    <Button type="submit" disabled={editSubmitting}>
                                        {editSubmitting ? t('actions.saving', 'common') : t('actions.saveChanges', 'common')}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentInstrumentsTab;
